import React, { useState, useEffect } from 'react';
import { PgCogDetails, ROOM_OPTIONS } from './RecommendationDetails/PgCogDetails';
import { DEVICE_SECTION_TITLE } from './RecommendationDetails/deviceOptionDefaults';
import { staleHeatSourceRefs, type PickableHeatSource } from '../utils/heatSourceNames';
import {
  buildAirflowText,
  buildText,
  hasDetails,
  parseRecommendationText,
  parseSelections,
  roomsForItem,
  serializeRecommendations,
  type RecommendationItem,
  type SavedEntry,
  type Selection,
} from '../utils/recommendationCatalog';
import { useDeviceOptionSets } from '../hooks/useDeviceOptionSets';
import { useRecommendationItems } from '../hooks/useRecommendationItems';
import { ModalShell } from '../ds/ModalShell';
import { ConfirmModal } from '../ds/ConfirmModal';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';

interface Props {
  /** Źródła ciepła lokalu (a gdy ich nie ma — wspólne budynku). Od 2026-10-09 służą tylko
   *  ostrzeżeniu przy STARYCH zapisach „— dotyczy: …" (źródło usunięte / zmieniony typ). */
  heatSources?: PickableHeatSource[];
  /** Nieużywane od 2026-10-09 (wybór źródeł zastąpiła lista urządzeń pozycji) — zostaje dla
   *  zgodności wywołań. */
  heatSourcesScope?: 'apartment' | 'building';
  visible: boolean;
  currentRecommendations: string;
  onSave: (recommendations: string) => void;
  onCancel: () => void;
}

/**
 * LUSTRO kanonu mobilnego (mobile/src/components/RecommendationsModal.tsx). Katalog pozycji:
 * utils/recommendationCatalog.ts (identyczny z mobilką) + API (migr. 059, zakładka „Zalecenia");
 * listy urządzeń: migr. 054. Zapis = wolny tekst (bloki rozdzielone pustą linią) — PDF i CEEB
 * biorą go dosłownie. Podformularz „Szczelne Mieszkanie" (dopływ) jest tylko w mobilce — tu
 * pozycja dodaje samą treść, a zapis z podformularzem z tabletu zostaje bez zmian.
 */
const sectionTitleStyle: React.CSSProperties = {
  fontSize: 'var(--text-body)',
  fontWeight: 'var(--weight-bold)',
  color: 'var(--text-heading)',
  margin: '0 0 var(--space-3) 0',
};

export const RecommendationsModal: React.FC<Props> = ({
  heatSources = [],
  visible,
  currentRecommendations,
  onSave,
  onCancel,
}) => {
  const [savedRecommendations, setSavedRecommendations] = useState<SavedEntry[]>([]);
  const [customText, setCustomText] = useState('');
  // „Inne uwagi" stały w zapisie przed pozycjami katalogu — zapis odtwarza tę kolejność.
  const [customFirst, setCustomFirst] = useState(false);
  // Katalog pozycji i listy „Typ urządzenia" z zakładki „Zalecenia" (API) → stałe z kodu
  const items = useRecommendationItems(visible);
  const deviceOptionSets = useDeviceOptionSets(visible);
  // Widok szczegółów: pozycja + wybór odtworzony z zapisu w chwili otwarcia (patrz handleDetailsSave).
  const [editing, setEditing] = useState<{ id: string; initial: Selection } | null>(null);
  // Podmiana treści zmienianej ręcznie czeka na potwierdzenie (ConfirmModal).
  const [pendingReplace, setPendingReplace] = useState<{ id: string; fullText: string } | null>(null);
  // Czy użytkownik zmienił już coś w oknie (wtedy późniejszy katalog nie przelicza zapisu od nowa).
  const [touched, setTouched] = useState(false);

  const findItem = (id: string): RecommendationItem | undefined => items.find((i) => i.id === id);

  // Odtworzenie zaznaczeń przy ponownym otwarciu okna (także pozycji wycofanych i starszych treści
  // pozycji). Tekst każdego bloku zostaje DOSŁOWNIE; nierozpoznane bloki wracają do „Inne uwagi".
  const parseExistingRecommendations = (text: string) => {
    const parsed = parseRecommendationText(text, items);
    setSavedRecommendations(parsed.entries);
    setCustomText(parsed.customText);
    setCustomFirst(parsed.customFirst);
  };

  useEffect(() => {
    if (visible) {
      parseExistingRecommendations(currentRecommendations);
      setTouched(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, currentRecommendations]);

  // Katalog z serwera dojeżdża PO otwarciu okna (np. pozycja dodana w panelu) — rozpoznajemy
  // zapis jeszcze raz, ale tylko dopóki użytkownik niczego nie zmienił.
  useEffect(() => {
    if (visible && !touched) parseExistingRecommendations(currentRecommendations);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const handleOptionClick = (item: RecommendationItem) => {
    if (hasDetails(item)) {
      const existing = savedRecommendations.find((r) => r.id === item.id);
      const initial = existing
        ? parseSelections(existing.fullText, item, deviceOptionSets[item.id] ?? [], ROOM_OPTIONS).selection
        : { devices: [], otherDevice: '', rooms: [], otherRoom: '' };
      setEditing({ id: item.id, initial });
      return;
    }
    setTouched(true);
    const existing = savedRecommendations.find((r) => r.id === item.id);
    if (existing) {
      setSavedRecommendations(savedRecommendations.filter((r) => r.id !== item.id));
    } else {
      const fullText = item.builtinKind === 'airflow'
        ? buildAirflowText(item, { closed: false, count: '' })
        : item.baseText;
      setSavedRecommendations([...savedRecommendations, { id: item.id, fullText }]);
    }
  };

  const applyDetails = (id: string, fullText: string) => {
    setTouched(true);
    setSavedRecommendations((prev) =>
      prev.some((r) => r.id === id)
        ? prev.map((r) => (r.id === id ? { ...r, fullText } : r))
        : [...prev, { id, fullText }]
    );
    setEditing(null);
  };

  const handleDetailsSave = (item: RecommendationItem, selection: Selection) => {
    if (!editing) return;
    const options = deviceOptionSets[item.id] ?? [];
    const fullText = buildText(item, selection, options, ROOM_OPTIONS);
    // Czy wybór, od którego widok wystartował, odtwarza zapisaną treść z AKTUALNĄ listą urządzeń.
    // Nie — bo treść zmieniano ręcznie, to starsza wersja tekstu albo lista dojechała z sieci już
    // po otwarciu (skrót z panelu, np. „MOP", nie odtworzył się z listy z cache) — wtedy podmiana
    // zgubiłaby część treści, więc tylko po potwierdzeniu.
    const baseline = buildText(item, editing.initial, options, ROOM_OPTIONS);
    const existing = savedRecommendations.find((r) => r.id === item.id);
    // Anulowanie pytania zostawia treść bez zmian.
    if (existing && existing.fullText !== fullText && baseline !== existing.fullText) {
      setPendingReplace({ id: item.id, fullText });
      return;
    }
    applyDetails(item.id, fullText);
  };

  const handleSave = () => {
    // Nic nie zmieniono w oknie → oddajemy zapis BAJT W BAJT (bez przeliczania kolejności bloków
    // i odstępów — ten tekst idzie do PDF i do CEEB).
    if (!touched) {
      onSave(currentRecommendations);
      return;
    }
    onSave(serializeRecommendations(savedRecommendations, customText, customFirst));
  };

  /** Ręczna korekta treści zaznaczonej pozycji — zapisuje się dosłownie (handleSave). */
  const updateRecommendationText = (id: string, text: string) => {
    setTouched(true);
    setSavedRecommendations((prev) => prev.map((r) => (r.id === id ? { ...r, fullText: text } : r)));
  };

  const handleRemove = (id: string) => {
    setTouched(true);
    setSavedRecommendations(savedRecommendations.filter((r) => r.id !== id));
  };

  if (!visible) return null;

  // Render detail view if editing
  const editingItem = editing ? findItem(editing.id) : undefined;
  if (editing && editingItem) {
    return (
      <>
        <ModalShell open onClose={onCancel} hideClose ariaLabel="Szczegóły zalecenia" width={760}>
          <PgCogDetails
            title={editingItem.label}
            deviceOptions={deviceOptionSets[editingItem.id] ?? []}
            devicesMode={editingItem.devicesMode}
            allowOtherDevice={editingItem.allowOtherDevice}
            deviceSectionTitle={DEVICE_SECTION_TITLE[editingItem.id]}
            rooms={roomsForItem(editingItem, ROOM_OPTIONS)}
            roomsMode={editingItem.roomsMode}
            allowOtherRoom={editingItem.allowOtherRoom}
            initialSelection={editing.initial}
            onSave={(selection) => handleDetailsSave(editingItem, selection)}
            onBack={() => setEditing(null)}
          />
        </ModalShell>
        <ConfirmModal
          open={pendingReplace !== null}
          title="Zastąpić treść zalecenia?"
          message="Zapisana treść różni się od wybranych pól (zmiana ręczna albo wcześniejsza wersja tekstu). Zapis z pól zastąpi ją w całości."
          confirmLabel="Zastąp"
          destructive
          onCancel={() => {
            setPendingReplace(null);
            setEditing(null);
          }}
          onConfirm={() => {
            const p = pendingReplace;
            setPendingReplace(null);
            if (p) applyDetails(p.id, p.fullText);
          }}
        />
      </>
    );
  }

  // Render main list
  return (
    <ModalShell
      open
      onClose={onCancel}
      title="Wybierz zalecenia"
      width={760}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Anuluj
          </Button>
          <Button variant="primary" onClick={handleSave} icon="check">
            Zapisz ({savedRecommendations.length})
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <p
          style={{
            margin: '0 0 var(--space-4) 0',
            fontSize: 'var(--text-label)',
            color: 'var(--text-secondary)',
          }}
        >
          {savedRecommendations.length} zalece{savedRecommendations.length === 1 ? 'nie' : 'ń'} wybrano
        </p>

        {/* Saved recommendations */}
        {savedRecommendations.length > 0 && (
          <>
            <h3 style={sectionTitleStyle}>Wybrane zalecenia:</h3>
            {savedRecommendations.map((rec) => {
              const item = findItem(rec.id);
              // Stary zapis „— dotyczy: …" wskazuje źródło, którego już nie ma — tylko ostrzegamy.
              const staleRefs = heatSources.length > 0 ? staleHeatSourceRefs(rec.fullText, heatSources) : [];
              return (
                <div
                  key={rec.id}
                  style={{
                    background: 'var(--status-ok-bg)',
                    border: `var(--border-width-strong) solid var(--status-ok-bar)`,
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--space-4)',
                    marginBottom: 'var(--space-3)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 'var(--space-3)',
                      marginBottom: 'var(--space-2)',
                    }}
                  >
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        flex: 1,
                        minWidth: 0,
                        fontSize: 'var(--text-label)',
                        fontWeight: 'var(--weight-bold)',
                        color: 'var(--status-ok)',
                      }}
                    >
                      <Icon name="check-circle" size={20} color="var(--status-ok)" />
                      {item?.label}
                      {item && !item.active && (
                        <span style={{ color: 'var(--text-muted)', fontWeight: 'var(--weight-semibold)' }}>(wycofane)</span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemove(rec.id)}
                      aria-label="Usuń zalecenie"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        flex: 'none',
                        padding: 0,
                        background: 'transparent',
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                        color: 'var(--status-deny)',
                        cursor: 'pointer',
                      }}
                    >
                      <Icon name="x" size={20} />
                    </button>
                  </div>
                  <textarea
                    value={rec.fullText}
                    onChange={(e) => updateRecommendationText(rec.id, e.target.value)}
                    aria-label={`Treść zalecenia: ${item?.label ?? ''}`}
                    rows={4}
                    style={{
                      display: 'block',
                      width: '100%',
                      boxSizing: 'border-box',
                      margin: 0,
                      padding: 'var(--space-3)',
                      fontFamily: 'inherit',
                      fontSize: 'var(--text-caption)',
                      lineHeight: 'var(--leading-normal)',
                      color: 'var(--text-primary)',
                      background: 'var(--surface-card)',
                      border: `var(--border-width) solid var(--status-ok-bar)`,
                      borderRadius: 'var(--radius-sm)',
                      resize: 'vertical',
                    }}
                  />
                  {staleRefs.length > 0 && (
                    <p role="alert" style={{ margin: 'var(--space-2) 0 0 0', fontSize: 'var(--text-caption)', fontWeight: 'var(--weight-semibold)', color: 'var(--status-rec)' }}>
                      Uwaga: zalecenie wskazuje źródło, którego nie ma już na liście źródeł ciepła
                      ({staleRefs.join(', ')}). Popraw treść albo usuń zalecenie i wybierz ponownie.
                    </p>
                  )}
                </div>
              );
            })}
          </>
        )}

        <h3 style={{ ...sectionTitleStyle, marginTop: 'var(--space-3)' }}>Dostępne zalecenia:</h3>

        {/* Wycofane pozycje nie są do wyboru — zostają tylko w starych zapisach (wyżej). */}
        {items.filter((item) => item.active).map((item) => {
          const isAdded = savedRecommendations.some((r) => r.id === item.id);
          // Zaznaczenie = zielona karta + wypełniony checkbox (kanon wyboru z mobilki,
          // BuildingDetailsScreen `recCardSelected`). Checkbox rysujemy ZAWSZE, żeby
          // widać było też to, co NIE jest zaznaczone.
          const cardBackground = isAdded ? 'var(--status-ok-bg)' : 'var(--surface-card)';
          return (
            <button
              key={item.id}
              type="button"
              aria-pressed={isAdded}
              onClick={() => handleOptionClick(item)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--space-3)',
                width: '100%',
                textAlign: 'left',
                padding: 'var(--space-4)',
                marginBottom: 'var(--space-3)',
                background: cardBackground,
                border: `var(--border-width-strong) solid ${isAdded ? 'var(--status-ok-bar)' : 'var(--border-strong)'}`,
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                transition: 'border-color .12s ease, background-color .12s ease',
                WebkitTapHighlightColor: 'transparent',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--surface-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = cardBackground;
              }}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  flex: 1,
                  minWidth: 0,
                  fontSize: 'var(--text-label)',
                  fontWeight: 'var(--weight-semibold)',
                  color: isAdded ? 'var(--status-ok)' : 'var(--text-primary)',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 'none',
                    width: 24,
                    height: 24,
                    borderRadius: 'var(--radius-sm)',
                    border: `var(--border-width-strong) solid ${isAdded ? 'var(--status-ok-bar)' : 'var(--border-strong)'}`,
                    background: isAdded ? 'var(--status-ok-bar)' : 'var(--surface-card)',
                  }}
                >
                  {isAdded && <Icon name="check" size={16} color="var(--text-on-action)" />}
                </span>
                {item.label}
              </span>
              {hasDetails(item) && (
                <Icon name="chevron-right" size={20} color="var(--action-primary)" />
              )}
            </button>
          );
        })}

        <h3 style={{ ...sectionTitleStyle, marginTop: 'var(--space-3)' }}>Inne uwagi:</h3>
        <textarea
          placeholder="Dodatkowe uwagi i zalecenia..."
          value={customText}
          onChange={(e) => {
            setTouched(true);
            setCustomText(e.target.value);
          }}
          rows={6}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: 'var(--space-3) var(--space-4)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-body)',
            lineHeight: 'var(--leading-normal)',
            color: 'var(--text-primary)',
            background: 'var(--surface-card)',
            border: 'var(--border-width-strong) solid var(--border-strong)',
            borderRadius: 'var(--radius-md)',
            resize: 'vertical',
            outline: 'none',
          }}
        />
      </div>
    </ModalShell>
  );
};
